import {
  Alert,
  AssetWatchLogo,
  BlockingActionOverlay,
  Button,
  Group,
  Stack,
  Text,
} from "@assetwatch/ui";
import { AccountCreatedScreen } from "./LoginFeedback";
import {
  BasicPasswordFields,
  DisabledPasswordField,
  EmailLookupFields,
  LoginHeader,
  MfaFields,
  NewPasswordFields,
  TemporaryPasswordFields,
} from "./LoginForms";
import { DEFAULT_CUSTOM_LOGIN_FORM_MESSAGES } from "./defaultMessages";
import type { LoginLookupResult } from "./loginLookup";
import { validateNewPassword } from "./loginUtils";
import type { PasswordRequirementLabels } from "./PasswordRequirements";
import { useLoginFlow } from "./useLoginFlow";

export interface CustomLoginFormProps {
  authenticate: () => Promise<void>;
  forgotPasswordHref?: string | ((email: string) => string);
  initialEmail?: string;
  initialStatusMessage?: string;
  isAuthenticated: boolean;
  lookupLoginMethod: (email: string) => Promise<LoginLookupResult>;
  messages?: CustomLoginFormMessages;
  onNavigate: (to: string) => void;
  passwordUpdated?: boolean;
  redirectTo?: string;
  supportHref?: string;
}

export type CustomLoginFormMessages = {
  accessibility: {
    assetWatchLogoAriaLabel: string;
    hidePassword: string;
    showPassword: string;
  };
  headings: {
    signInTitle: string;
    signInDescription: string;
    updatePasswordTitle?: string;
    updatePasswordDescription?: string;
    accountCreatedTitle?: string;
    accountCreatedDescription?: string;
    twoFactorTitle: string;
    twoFactorDescription: string;
  };
  labels: {
    email: string;
    password: string;
    newPassword: string;
    confirmPassword: string;
    verificationCode: string;
  };
  placeholders: {
    email: string;
  };
  actions: {
    rememberMe: string;
    continue: string;
    forgotPassword: string;
    reachOutToSupport?: string;
    login: string;
    loginWithSso: string;
    retry?: string;
    saveChanges?: string;
    startMonitoring?: string;
    setNewPassword: string;
    changeEmail: string;
    submit: string;
  };
  loading: {
    checking: string;
    updatingPassword: string;
    loggingIn: string;
    verifying: string;
    redirecting: string;
    ssoRedirecting?: string;
  };
  copy: {
    newPasswordPrompt: string;
    passwordRequirementsHeading?: string;
    temporaryPasswordPrompt?: string;
    temporaryPasswordHelp?: string;
    confirmPasswordMismatch: string;
    supportQuestion?: string;
  };
  status: {
    passwordUpdatedSuccess: string;
    temporaryPasswordUpdatedSuccess?: string;
  };
  errors: {
    invalidEmail: string;
    incorrectEmailOrPassword: string;
    accountNotConfirmed: string;
    passwordResetRequired: string;
    tooManyAttempts: string;
    network: string;
    generic: string;
    passwordHistoryViolation: string;
    invalidPasswordPolicy: string;
    updatePasswordGeneric: string;
    invalidMfaCode: string;
    identityProviderUndefined: string;
    unsupportedSignInStep: string;
  };
  passwordRequirements: PasswordRequirementLabels;
};

export type AuthType = "" | "basic" | "temporaryPassword" | "sso" | "mfacode";

export type StatusTone = "positive" | "negative" | "informative";

export { DEFAULT_CUSTOM_LOGIN_FORM_MESSAGES } from "./defaultMessages";

export function CustomLoginForm({
  authenticate,
  forgotPasswordHref,
  initialEmail = "",
  initialStatusMessage,
  isAuthenticated,
  lookupLoginMethod,
  messages: messagesProp,
  onNavigate,
  passwordUpdated = false,
  redirectTo = "/",
  supportHref,
}: CustomLoginFormProps) {
  const {
    authType,
    checkEmail,
    confirmPassword,
    confirmPasswordVisibilityLabel,
    email,
    emailError,
    firstSignIn,
    forgotPasswordUrl,
    handleSubmit,
    isAccountCreated,
    isCheckingEmail,
    isConfirmPasswordVisible,
    isFormValid,
    isLoading,
    isNewPasswordVisible,
    isPasswordVisible,
    isRememberMeEnabled,
    isSsoRedirecting,
    isTemporaryPasswordChallenge,
    messages,
    mfaCode,
    newPassword,
    newPasswordError,
    newPasswordVisibilityLabel,
    passedPasswordHistory,
    password,
    passwordError,
    passwordValidation,
    passwordVisibilityLabel,
    resetAuthState,
    setConfirmPassword,
    setEmail,
    setEmailError,
    setIsConfirmPasswordVisible,
    setIsNewPasswordVisible,
    setIsPasswordVisible,
    setIsRememberMeEnabled,
    setMfaCode,
    setNewPassword,
    setNewPasswordError,
    setPassedPasswordHistory,
    setPassword,
    setPasswordError,
    setPasswordValidation,
    startMonitoring,
    statusMessage,
    statusTone,
  } = useLoginFlow({
    authenticate,
    forgotPasswordHref,
    initialEmail,
    initialStatusMessage,
    isAuthenticated,
    lookupLoginMethod,
    messages: messagesProp,
    onNavigate,
    passwordUpdated,
    redirectTo,
    supportHref,
  });
  const supportLabel =
    messages.actions.reachOutToSupport ??
    DEFAULT_CUSTOM_LOGIN_FORM_MESSAGES.actions.reachOutToSupport;

  return (
    <div className="w-full bg-surface-low">
      <BlockingActionOverlay
        label={messages.loading.ssoRedirecting ?? ""}
        open={isSsoRedirecting}
      >
        <div className="relative flex min-h-screen w-full flex-col items-stretch">
          <header className="flex h-(--spacing-64) w-full items-center border-b border-outline-subtle bg-surface px-(--spacing-16)">
            <AssetWatchLogo
              aria-label={messages.accessibility.assetWatchLogoAriaLabel}
            />
          </header>
          <div className="flex w-full flex-1 justify-center px-(--spacing-20) pt-(--spacing-40) pb-(--spacing-24) sm:pt-(--spacing-80) sm:pb-(--spacing-40)">
            <form
              className="w-full max-w-[480px]"
              data-testid="login-form"
              noValidate
              onSubmit={(event) => {
                event.preventDefault();
                if (authType === "") {
                  void checkEmail();
                } else if (authType === "sso" || isFormValid()) {
                  void handleSubmit();
                }
              }}
            >
              <Stack gap={40}>
                {isAccountCreated ? (
                  <AccountCreatedScreen
                    isLoading={isLoading}
                    messages={messages}
                    onStartMonitoring={() => void startMonitoring()}
                  />
                ) : (
                  <>
                    <LoginHeader
                      authType={authType}
                      firstSignIn={firstSignIn}
                      isTemporaryPasswordChallenge={
                        isTemporaryPasswordChallenge
                      }
                      messages={messages}
                    />

                    {statusMessage ? (
                      <Alert tone={statusTone} title={statusMessage} />
                    ) : null}

                    <Stack gap={16}>
                      <EmailLookupFields
                        authType={authType}
                        email={email}
                        emailError={emailError}
                        isCheckingEmail={isCheckingEmail}
                        isRememberMeEnabled={isRememberMeEnabled}
                        messages={messages}
                        onCheckEmail={() => void checkEmail()}
                        onEmailChange={(value) => {
                          setEmail(value);
                          setEmailError(null);
                        }}
                        onRememberMeChange={setIsRememberMeEnabled}
                      />

                      {authType === "temporaryPassword" && !firstSignIn ? (
                        <TemporaryPasswordFields
                          canSubmit={isFormValid()}
                          email={email}
                          isLoading={isLoading}
                          isPasswordVisible={isPasswordVisible}
                          messages={messages}
                          onPasswordChange={(value) => {
                            setPassword(value);
                            setPasswordError(null);
                          }}
                          onPasswordVisibilityChange={() =>
                            setIsPasswordVisible((current) => !current)
                          }
                          password={password}
                          passwordError={passwordError}
                          passwordVisibilityLabel={passwordVisibilityLabel}
                        />
                      ) : null}

                      {authType === "basic" ? (
                        !firstSignIn ? (
                          <BasicPasswordFields
                            canSubmit={isFormValid()}
                            email={email}
                            forgotPasswordUrl={forgotPasswordUrl}
                            isLoading={isLoading}
                            isPasswordVisible={isPasswordVisible}
                            messages={messages}
                            onChangeEmail={() => resetAuthState()}
                            onPasswordChange={(value) => {
                              setPassword(value);
                              setPasswordError(null);
                            }}
                            onPasswordVisibilityChange={() =>
                              setIsPasswordVisible((current) => !current)
                            }
                            password={password}
                            passwordError={passwordError}
                            passwordVisibilityLabel={passwordVisibilityLabel}
                          />
                        ) : (
                          <DisabledPasswordField
                            messages={messages}
                            password={password}
                          />
                        )
                      ) : null}

                      {firstSignIn ? (
                        <NewPasswordFields
                          canSubmit={isFormValid()}
                          confirmPassword={confirmPassword}
                          confirmPasswordVisibilityLabel={
                            confirmPasswordVisibilityLabel
                          }
                          isConfirmPasswordVisible={isConfirmPasswordVisible}
                          isLoading={isLoading}
                          isNewPasswordVisible={isNewPasswordVisible}
                          isTemporaryPasswordChallenge={
                            isTemporaryPasswordChallenge
                          }
                          messages={messages}
                          newPassword={newPassword}
                          newPasswordError={newPasswordError}
                          newPasswordVisibilityLabel={
                            newPasswordVisibilityLabel
                          }
                          onConfirmPasswordChange={(value) => {
                            setConfirmPassword(value);
                            setPasswordValidation((current) => ({
                              ...current,
                              matchesConfirm: value === newPassword,
                            }));
                          }}
                          onConfirmPasswordVisibilityChange={() =>
                            setIsConfirmPasswordVisible((current) => !current)
                          }
                          onNewPasswordChange={(value) => {
                            setNewPassword(value);
                            setNewPasswordError(null);
                            setPasswordValidation(
                              validateNewPassword(value, confirmPassword),
                            );
                            setPassedPasswordHistory((current) =>
                              current === false ? false : undefined,
                            );
                          }}
                          onNewPasswordVisibilityChange={() =>
                            setIsNewPasswordVisible((current) => !current)
                          }
                          passedPasswordHistory={passedPasswordHistory}
                          passwordValidation={passwordValidation}
                        />
                      ) : null}

                      {authType === "mfacode" ? (
                        <MfaFields
                          canSubmit={isFormValid()}
                          isLoading={isLoading}
                          messages={messages}
                          mfaCode={mfaCode}
                          onMfaCodeChange={setMfaCode}
                        />
                      ) : null}
                    </Stack>
                  </>
                )}
              </Stack>
            </form>
          </div>
          {supportHref &&
          !(
            (authType === "temporaryPassword" && !firstSignIn) ||
            isAccountCreated
          ) ? (
            <footer className="w-full px-(--spacing-20) pb-(--spacing-24) sm:pb-(--spacing-40)">
              <Group gap={0} justify="center">
                <Text tone="subtext">{messages.copy.supportQuestion}</Text>
                <Button
                  children={supportLabel}
                  icon="arrow-up-right"
                  render={<a href={supportHref} aria-label={supportLabel} />}
                  size="sm"
                  variant="ghost"
                />
              </Group>
            </footer>
          ) : null}
        </div>
      </BlockingActionOverlay>
    </div>
  );
}
